function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import get from 'lodash/get';
import pick from 'lodash/pick';
import { AppContext } from 'context';
import MetricConversions from 'libs/MetricConversions';
import Validator from 'libs/Validator';
import { getStandards } from 'helpers/standards';
import intlShape from 'shapes/intlShape';
import FormContainerAbstract from 'components/FormContainerAbstract';
import Form from 'components/Form';
import FormGroup from 'components/Form/FormGroup';
import AutosuggestInput from 'components/Form/AutosuggestInput';
import Button from 'components/Form/Button';
import PhiSetFormPartial from 'components/PhiSetFormPartial';
import countrySettingsShape from 'shapes/countrySettingsShape';
import App from 'modules/App';
import Account from 'modules/Account';
import CloudDrive from 'modules/CloudDrive';
import ContourCloud from 'modules/ContourCloud';
import history from 'helpers/history';
import { addMetricUnitMaxValueRules } from 'helpers/patientProfileValidatorRules';
import * as actions from '../../../../actions';
import * as constants from '../../../../constants';
import * as selectors from '../../../../selectors';
import styles from '../../Profile.pcss';
import validatorRules from './validatorRules.json';
class PatientProfile extends FormContainerAbstract {
    static getDerivedStateFromProps(props, state) {
        if (props.isInProgress !== state.isInProgress && (props.isInProgress || !props.isInProgress && props.hasErrors)) {
            return {
                isInProgress: props.isInProgress
            };
        }
        return null;
    }
    componentDidMount() {
        const { account, passphrase } = this.props;
        this.onPopulate();
        this.props.onFetchPayers();
        this.props.onGetCCAccessToken(account, passphrase);
        this.props.onFetchCountrySettings();
        this.props.onFetchHealthDataFromInvitation();
    }
    componentDidUpdate(prevProps) {
        const { accountCCAccessToken, patientProfile, isInProgress } = this.props;
        if (prevProps.isInProgress !== isInProgress && patientProfile && patientProfile.encryptedExchangeToken) {
            history.push(this.context.getUrl('my-results'));
            return;
        }
        if (prevProps.accountCCAccessToken !== accountCCAccessToken && accountCCAccessToken) {
            this.props.onFetchCCProfile();
        }
        if (prevProps.patientHealthDataFromInvitation !== this.props.patientHealthDataFromInvitation && this.props.patientHealthDataFromInvitation) {
            this.onPopulateHealthData();
        }
        if (this.props.profileCC && prevProps.profileCC !== this.props.profileCC) {
            this.onPopulate();
        }
    }
    componentWillUnmount() {
        if (this.props.route.name !== 'setup') this.props.onClearForm();
    }
    onPopulate() {
        const { formValues } = this.props;
        const filledFormValues = get(formValues, 'values', {});
        const values = _object_spread({}, filledFormValues, pick(this.props.profileCC, [
            'diabetesType',
            'treatmentType'
        ]));
        this.onSetValues(values);
    }
    onPopulateHealthData() {
        const { patientHealthDataFromInvitation } = this.props;
        const { weight, height } = patientHealthDataFromInvitation;
        patientHealthDataFromInvitation.weight = weight && this.metricConversions.weight.toDisplay(weight);
        patientHealthDataFromInvitation.height = height && this.metricConversions.height.toDisplay(height);
        this.onSetValues(patientHealthDataFromInvitation);
    }
    onValidate(rules) {
        const values = _object_spread({}, get(this.props.formValues, 'values', {}));
        const clonedRules = _object_spread({}, rules);
        if (this.isPayerMandatory) {
            clonedRules.payer = 'required';
        }
        return Validator.run(values, clonedRules);
    }
    onSubmit() {
        this.props.onFormProcessing();
        const rules = addMetricUnitMaxValueRules(this.validatorRules, this.metricConversions);
        const { validatedValues, errors } = this.onValidate(rules);
        this.props.onFormErrors(errors);
        if (!errors) {
            const { weight, height } = validatedValues;
            validatedValues.weight = weight && this.metricConversions.weight.toStorage(weight);
            validatedValues.height = height && this.metricConversions.height.toStorage(height);
            const standards = getStandards(null, this.props.countrySettings);
            const { payer } = validatedValues;
            const { authorizationCode, enrollCode, invitationCode } = this.props;
            this.props.onSubmit(validatedValues, authorizationCode, standards, enrollCode, invitationCode);
            if (payer) {
                const { countryId } = this.props.account;
                this.props.onAddPayerProposal(countryId, payer);
            }
        }
    }
    get isPayerMandatory() {
        const { countrySettings } = this.props;
        return countrySettings ? countrySettings.isPayerMandatory : false;
    }
    get isDisabled() {
        const { authorizationCode } = this.props;
        const isCentralStorageSet = authorizationCode || this.props.isCentralStorageSet;
        const { errors } = this.onValidate(this.validatorRules);
        return !isCentralStorageSet || !!errors;
    }
    get isDisabledTreatmentType() {
        const { profileCC } = this.props;
        if (!profileCC) return false;
        return Boolean(profileCC.treatmentType);
    }
    get isDisabledDiabetesType() {
        const { profileCC } = this.props;
        if (!profileCC) return false;
        return Boolean(profileCC.diabetesType);
    }
    renderActions() {
        return /*#__PURE__*/ _jsx("div", {
            className: "row",
            children: /*#__PURE__*/ _jsx("div", {
                className: "col-6",
                children: /*#__PURE__*/ _jsx(Button, {
                    type: "submit",
                    styleModifier: "primary",
                    labelMessage: Account.messages.buttons.submit,
                    className: "btn--block btn--filled",
                    isDisabled: this.isDisabled,
                    isInProgress: this.state.isInProgress,
                    onClickDisabled: ()=>this.onCheck()
                })
            })
        });
    }
    renderPayer() {
        if (!this.isPayerMandatory) {
            return null;
        }
        return /*#__PURE__*/ _jsx(FormGroup, {
            id: "payer",
            labelMessage: Account.messages.labels.payer,
            formValues: this.props.formValues,
            children: /*#__PURE__*/ _jsx(AutosuggestInput, {
                suggestions: this.props.payers,
                placeholder: Account.messages.placeholders.payer,
                onChange: (input)=>this.onSetValue(input)
            })
        });
    }
    renderForm() {
        return /*#__PURE__*/ _jsxs(Form, {
            onSubmit: ()=>this.onSubmit(),
            children: [
                /*#__PURE__*/ _jsx(PhiSetFormPartial, {
                    metricConversions: this.metricConversions,
                    formValues: this.props.formValues,
                    onSetFormValue: this.props.onSetFormValue,
                    hasCentralStorage: this.hasCentralStorage,
                    isDisabledDiabetesType: this.isDisabledDiabetesType,
                    isDisabledTreatmentType: this.isDisabledTreatmentType,
                    localizationResources: this.props.localizationResources,
                    isActiveProfileTypePwd: Account.constants.PROFILE_TYPES.PWD === this.props.activeProfileType
                }),
                this.renderActions()
            ]
        });
    }
    render() {
        return /*#__PURE__*/ _jsxs("div", {
            "data-testid": "fill-health-info-section",
            children: [
                /*#__PURE__*/ _jsx("header", {
                    className: "pageHeader",
                    children: /*#__PURE__*/ _jsx("h1", {
                        className: "text--h1",
                        children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, Account.messages.headers.fillHealthInformation))
                    })
                }),
                this.renderAlerts(),
                this.renderForm()
            ]
        });
    }
    constructor(props){
        super(props);
        this.metricConversions = new MetricConversions(props.metricsUnits);
        this.state = {
            isInProgress: false
        };
        this.hasCentralStorage = !!props.walletEntriesList.find((entry)=>entry.serviceName === 'CentralStorage');
        this.validatorRules = _object_spread({}, validatorRules);
        if (this.hasCentralStorage) {
            delete this.validatorRules.diabetesType;
            delete this.validatorRules.treatmentType;
        }
    }
}
_define_property(PatientProfile, "contextType", AppContext);
_define_property(PatientProfile, "propTypes", _object_spread_props(_object_spread({
    // Explicit props
    enrollCode: PropTypes.string,
    invitationCode: PropTypes.string,
    isCentralStorageSet: PropTypes.bool
}, FormContainerAbstract.propTypes), {
    account: Account.shapes.account,
    accountCCAccessToken: PropTypes.object,
    passphrase: PropTypes.string,
    patientProfile: PropTypes.object,
    authorizationCode: CloudDrive.shapes.authorizationCode,
    patientHealthDataFromInvitation: Account.shapes.patientHealthDataFromInvitation,
    payers: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired
    })),
    localizationResources: PropTypes.object,
    route: App.shapes.route,
    metricsUnits: PropTypes.object,
    intl: intlShape,
    countrySettings: countrySettingsShape,
    profileCC: PropTypes.object,
    activeProfileType: Account.shapes.profileType,
    walletEntriesList: PropTypes.array,
    // Implicit actions
    onFetchCountrySettings: PropTypes.func,
    onGetCCAccessToken: PropTypes.func,
    onFetchCCProfile: PropTypes.func,
    onFetchHealthDataFromInvitation: PropTypes.func,
    onAddPayerProposal: PropTypes.func,
    onFetchPayers: PropTypes.func
}));
const patientProfileFormSelector = App.selectors.formSelector(constants.PATIENT_PROFILE_FORM);
const mapStateToProps = (state)=>({
        isInProgress: selectors.isCreatePatientProfileInProgress(state),
        hasErrors: selectors.hasCreatePatientProfileErrors(state),
        account: Account.selectors.account(state),
        accountCCAccessToken: Account.selectors.accountCCAccessToken(state),
        passphrase: Account.selectors.passphrase(state),
        patientProfile: Account.selectors.patientProfile(state),
        patientDataFromInvitation: Account.selectors.patientDataFromInvitation(state),
        patientHealthDataFromInvitation: Account.selectors.patientHealthDataFromInvitation(state),
        payers: Account.selectors.payers(state),
        metricsUnits: Account.selectors.metricsUnits(state),
        countrySettings: Account.selectors.countrySettings(state),
        activeProfileType: Account.selectors.activeProfileType(state),
        walletEntriesList: Account.selectors.walletEntriesList(state),
        localizationResources: App.selectors.localizationResources(state),
        formValues: patientProfileFormSelector(state),
        route: App.selectors.route(state),
        authorizationCode: CloudDrive.selectors.authorizationCode(state),
        profileCC: ContourCloud.selectors.profile(state)
    });
const mapDispatchToProps = (dispatch)=>{
    const formName = constants.PATIENT_PROFILE_FORM;
    return {
        onSubmit: (values, authorizationCode, standards, enrollCode, invitationCode)=>dispatch(actions.createPatientProfile(values, authorizationCode, standards, enrollCode, invitationCode)),
        onFetchHealthDataFromInvitation: ()=>dispatch(Account.actions.fetchPatientHealthDataFromInvitation()),
        onGetCCAccessToken: (account, passphrase)=>dispatch(Account.actions.getCCAccessToken({
                account,
                passphrase
            })),
        onFetchCountrySettings: ()=>dispatch(Account.actions.fetchCountrySettings()),
        onFetchPayers: ()=>dispatch(Account.actions.fetchPayers()),
        onFetchCCProfile: ()=>dispatch(ContourCloud.actions.getMe()),
        onAddPayerProposal: (countryId, payer)=>dispatch(App.actions.addPayerProposal(countryId, payer)),
        onSetFormValue: (input)=>dispatch(App.actions.setFormValue(formName, input)),
        onSetFormValues: (values)=>dispatch(App.actions.setFormValues(formName, values)),
        onFormErrors: (errors)=>dispatch(App.actions.setFormErrors(formName, errors)),
        onFormProcessing: ()=>dispatch(App.actions.startFormProcessing(formName)),
        onClearForm: ()=>dispatch(App.actions.clearForm(formName))
    };
};
const ConnectedPatientProfile = connect(mapStateToProps, mapDispatchToProps)(injectIntl(PatientProfile));
export default withStyles(styles)(ConnectedPatientProfile);
